/* eslint-disable max-len */
import { useEffect } from 'react';

// https://developers.google.com/analytics/devguides/collection/gtagjs/events

const browser = typeof window !== 'undefined';
const production = process.env.GATSBY_ENV === 'production';

export const getGaSessionId = () => {
  if (!process.env.GATSBY_READ_GA_SESSION_ID) {
    return null;
  }
  if (typeof document === 'undefined') {
    return null;
  }
  const gaCookie = document?.cookie?.split('; ').find((cookie) => cookie.startsWith('_ga='));
  if (gaCookie) {
    const gaValue = gaCookie.split('=')[1];
    return gaValue.split('.')[2];
  }
  return null;
};

export const event = (action, data = {}) => {
  if (!browser) {
    return false;
  }
  if (!production) {
    console.log(`[helpers gtag] not in production, would send action ${action} with data`, data);
    return false;
  }
  if (!window.gtag) {
    return false;
  }
  window.gtag(
    'event',
    action, // https://developers.google.com/tag-platform/gtagjs/reference/events
    { ...data },
  );
  return true;
};

const convertVehicleToFields = ({
  vin,
  vehicle_id,
  lowest_price,
  is_new_vehicle,
  buildyear,
  make,
  model,
  type,
  color,
  base_color,
  vehicle_type,
  fuel,
  date_of_arrival,
}) => ({
  item_id: vin, // item identification number (i.e. VIN# or Serial#)
  item_number: vehicle_id, // item stock number assigned by dealer
  item_price: lowest_price, // MSRP for New Vehicles | Dealer price for used
  item_condition: is_new_vehicle ? 'new' : 'used', // new, used, salvage, dealer_cpo, oem_cpo, cpo, unknown
  item_year: buildyear, // model year
  item_make: make, // Chevrolet, Ford, Nissan, Stellantis, Land Rover, Jaguar, Buick, GMC, Volvo, Mercedes-Benz, Audi, BMW, Alfa Romeo, Fiat, MINI, Toyota, Honda, Acura, Lexus
  item_model: model, // model of product
  item_variant: type, // trim level of model
  item_color: color || base_color, // color of product
  item_type: vehicle_type, // sedan, coupe, suv, cuv, crossover, truck, commercial_truck, van, trailer, 5th_wheel, luxury, domestic,
  item_category: undefined, // luxury, domestic, foreign, economy, accessories, unknown
  item_fuel_type: fuel, // gas, diesel, liquified_natural_gas, electric, propane, nitrogen, methane, unknown
  item_inventory_date: date_of_arrival, // date item was entered into inventory
});

const useFn = (fn, value, shouldMatch, data) => {
  useEffect(() => {
    if (value === shouldMatch) {
      fn(data);
    }
  }, [fn, value, shouldMatch, data]);
};

export const click = (data = {}) => event('click', data);
export const useClick = (...args) => useFn(click, ...args);

// https://docs.google.com/spreadsheets/d/15JoQ7stjsjxWjf1qznwY-ApsmQesNTpPZbxLLBtcAQQ/edit#gid=837660608
export const ascCtaInteraction = ({
  event_owner = 'web', // Who is pushing the event
  page_type, // item, itemlist, contact, directions, service, parts, accessories, body_shop, home, build_to_order, custom, complaint, employment, recall, finance, credit, trade, commercial, showroom
  element_text, // whatever is shown on the CTA button / image
  element_color, // color of CTA button, if available or applicable
  element_order, // represents the order of the cta buttons, if known
  element_type, // header, banner, popup, form, digital_retailing_tool, trade_tool, chat_tool, accessibility_tool, search_tool, scheduling_tool, gallery, map, footer, video, item_details
  event_action_result, // play, restart, vr_play, spin, popup, slideout, exit, error, search, calc, start, stop, redirect, open, close, increase, decrease, clear, check, uncheck
  link_url, // destination URL, if applicable
}) => event('asc_cta_interaction', {
  event_owner,
  page_type,
  element_text,
  element_color,
  element_order,
  element_type,
  event_action_result,
  link_url,
});
export const useAscCtaInteraction = (...args) => useFn(ascCtaInteraction, ...args);

// https://docs.google.com/spreadsheets/d/15JoQ7stjsjxWjf1qznwY-ApsmQesNTpPZbxLLBtcAQQ/edit#gid=568534649
const defaultFormEventName = 'asc_form_submission';
export const ascFormSubmission = ({
  event_owner = 'web', // Who is pushing the event
  page_type, // item, itemlist, contact, directions, service, parts, accessories, body_shop, home, build_to_order, custom, complaint, employment, recall, finance, credit, trade, commercial, showroom
  comm_type = 'form', // chat, sms, voice, video, form, email
  comm_outcome = 'crm_update', // start, close, engage, inject, send_deal, send_payment, send_appt, directions, idle, timeout, load, video_start, video_end, connect, ring, appt_set, crm_update, appt_booked, blocked, failed, connected
  submission_id, // numeric counter (if assigned)
  element_text, // CTA TEXT (if available)
  department, // sales, service, parts, trade, credit, body_shop, admin, accessories, hr, custom_shop, rentals, unknown, quick_lube
  form_name, // the name of the form your platform uses to identify the form
  form_type, // item, consumer_contact, trade, recall, parts, accessories, service_appointment, pre-order, finance_credit, human_resources, body_shop, custom, research, surveys, unknown
  vehicle,
}, eventName = defaultFormEventName) => {
  const data = {
    event_owner,
    page_type,
    comm_type,
    comm_outcome,
    submission_id,
    element_text,
    department,
    form_name,
    form_type,
    ...(vehicle ? convertVehicleToFields(vehicle) : {}),
  };
  event(eventName, data);
  if (eventName !== defaultFormEventName) {
    // always send to the default form submission event
    event(defaultFormEventName, data);
  }
};

export const ascFormSubmissionSales = (data) => ascFormSubmission(data, 'asc_form_submission_sales');
export const ascFormSubmissionSalesAppt = (data) => ascFormSubmission(data, 'asc_form_submission_sales_appt');

// https://docs.google.com/spreadsheets/d/15JoQ7stjsjxWjf1qznwY-ApsmQesNTpPZbxLLBtcAQQ/edit#gid=527088468
export const ascMediaInteraction = ({
  event_owner = 'web', // Who is pushing the event
  page_type, // item, itemlist, contact, directions, service, parts, accessories, body_shop, home, build_to_order, custom, complaint, employment, recall, finance, credit, trade, commercial, showroom
  event_action_result, // play, restart, vr_play, spin, popup, slideout, exit, error, search, calc, start, stop, redirect, open, close, increase, decrease, clear, check, uncheck, next_step
  affiliation, // Name of company if different from website company
  media_type, // video, image, vr, slider, gallery, offer, 360_platform
  link_url, // allows each image (for example)  or video to have a unique hyperlink, if applicable
  creative_name, // allows each image (for example) to have its own name
}) => event('asc_media_interaction', {
  event_owner,
  page_type,
  event_action_result,
  affiliation,
  media_type,
  link_url,
  creative_name,
});

export const ascMenuInteraction = ({
  event_owner = 'web', // Who is pushing the event
  page_type, // item, itemlist, contact, directions, service, parts, accessories, body_shop, home, build_to_order, custom, complaint, employment, recall, finance, credit, trade, commercial, showroom
  element_text, // The text on the link that they clicked
  element_subtype, // cta_button, cta_icon, nav_button, nav_icon, nav_tab, hamburger_menu, mega_menu, sidebar_menu, inline_menu, hyperlink, content, share_icon, image, input_field, toggle, dropdown,
  element_type, // The menu tree that this menu item was under, since dealers can duplicate menu choices
  event_action, // click, slide, open, close, exit, increase, decrease, clear, check, uncheck, redirect
}) => event('asc_menu_interaction', {
  event_owner,
  page_type,
  element_text,
  element_subtype,
  element_type,
  event_action,
});

export default null;
